import { Popconfirm } from 'antd'
import DocumentTag from 'components/UI/DocumentTag'
import { API_DOCUMENT, API_DOCUMENTS_WISH } from 'constants/api-v2'
import { useI18n } from 'context/I18nProvider'
import { useQuery } from 'context/QueryProvider'
import { useToast } from 'context/ToastProvider'
import { useBlob } from 'hooks/binary'
import * as React from 'react'
import { iriToId } from 'services/apiPlatform'

interface TrainingWish {
  id: number
  '@id': string
}

export interface Document {
  '@id': string
  '@type': string
  name: string
  fileName: string
  originalName: string
  url: string
  extension: string
  updatedAt: string
  readonly: boolean
  trainingWish: TrainingWish
}
interface UserDocumentProps {
  callback?: () => void
  handleFileSelect?: (files: Document[]) => void
  selectedFiles?: Document[]
}

export default function DocumentWishTag({ callback, ...props }: Document & UserDocumentProps) {
  const { client } = useQuery()
  const { i18n } = useI18n()
  const toast = useToast()
  const { downloadBlob } = useBlob()

  // props cleanup
  const {
    fileName,
    originalName,
    '@type': unused,
    updatedAt,
    url,
    handleFileSelect,
    selectedFiles,
    readonly,
    trainingWish,
    ...rest
  } = props

  const [open, setOpen] = React.useState<boolean>(false)
  const [loading, setLoading] = React.useState<boolean>(false)

  const handleDownload = () => {
    downloadBlob(url).catch((e: Error) => toast.error(e))
  }

  const handleDelete = (iri: string) => {
    if (readonly) return

    const urlToDelete = trainingWish
      ? `${API_DOCUMENTS_WISH}/${iriToId(iri)}?trainingWishes[]=${trainingWish.id}`
      : `${API_DOCUMENT}/${iriToId(iri)}}`
    setLoading(true)
    client
      .delete(urlToDelete)
      .catch((e: Error) => toast.error(e))
      .finally(() => {
        if (selectedFiles && handleFileSelect) {
          handleFileSelect(selectedFiles.filter(file => file['@id'] !== props['@id']))
        }
        setLoading(false)
        callback?.()
      })
  }

  return (
    <Popconfirm
      title={i18n('label.attention')}
      description={i18n('label.document-delete-confirm')}
      open={open}
      onConfirm={() => handleDelete(props['@id'])}
      onCancel={() => setOpen(false)}
      okButtonProps={{ loading }}
    >
      <DocumentTag
        className="mb-2 cursor-pointer"
        {...rest}
        handleClick={() => handleDownload()}
        onClose={(e: Parameters<NonNullable<React.ComponentProps<typeof DocumentTag>['onClose']>>[0]) => {
          e.preventDefault()
          setOpen(true)
        }}
        closable={!readonly}
      />
    </Popconfirm>
  )
}
